import { useLayoutEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useT } from '../../i18n/i18n';
import { Icon } from './Icon';
import { SheetShell } from './Sheet';

// Form fields (03 / 05): eyebrow label (+ "Required" / template action on the right), white
// input, 1px border, 10px radius, 48px tall; textarea min 96px; focus = 2px primary.

export function Field(props: { label: string; required?: boolean; hint?: string; htmlFor?: string; trailing?: ReactNode; children: ReactNode; testId?: string }) {
  const { t } = useT();
  return (
    <div className="field" data-testid={props.testId}>
      <div className="field-head">
        <label className="field-label" htmlFor={props.htmlFor}>
          {props.label}
        </label>
        {props.trailing ?? (props.required ? <span className="field-required">{t('common.required')}</span> : null)}
      </div>
      {props.children}
      {props.hint ? <p className="field-hint">{props.hint}</p> : null}
    </div>
  );
}

export function TextInput(props: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'number';
  inputMode?: 'text' | 'numeric' | 'email' | 'tel' | 'decimal';
  disabled?: boolean;
  autoComplete?: string;
  testId?: string;
}) {
  return (
    <input
      id={props.id}
      className="field-input"
      type={props.type ?? 'text'}
      inputMode={props.inputMode}
      value={props.value}
      placeholder={props.placeholder}
      disabled={props.disabled}
      autoComplete={props.autoComplete}
      onChange={(event: ChangeEvent<HTMLInputElement>) => props.onChange(event.target.value)}
      data-testid={props.testId}
    />
  );
}

// The tallest an auto-growing textarea gets before it scrolls inside (about 12 lines at the field's 1.5 line height).
export const TEXTAREA_AUTO_GROW_MAX_PX = 320;

// Fits a textarea's height to its text (2026-10-28, device walkthrough B3: the English report sentence showed
// three lines and clipped the fourth behind an inner scroll). An element that is not laid out (hidden, or a
// test DOM) reports no scroll height and is left alone.
export function fitTextAreaHeight(el: HTMLTextAreaElement, maxPx: number = TEXTAREA_AUTO_GROW_MAX_PX): void {
  el.style.height = 'auto';
  const content = el.scrollHeight;
  if (!(content > 0)) {
    el.style.height = '';
    return;
  }
  const style = window.getComputedStyle(el);
  const wanted = content + (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  el.style.height = `${Math.min(wanted, maxPx)}px`;
  el.style.overflowY = wanted > maxPx ? 'auto' : 'hidden';
}

export function TextArea(props: { id?: string; value: string; onChange: (value: string) => void; placeholder?: string; rows?: number; disabled?: boolean; testId?: string; autoGrow?: boolean; compact?: boolean; onFocus?: () => void }) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const autoGrow = props.autoGrow === true;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!autoGrow || !el) return;
    const fit = () => fitTextAreaHeight(el);
    fit();
    // the width changes on rotation / iPad split view, and with it the number of lines
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [autoGrow, props.value]);
  return (
    <textarea
      ref={ref}
      id={props.id}
      className="field-textarea"
      value={props.value}
      placeholder={props.placeholder}
      rows={props.compact === true ? 1 : (props.rows ?? 4)}
      disabled={props.disabled}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => props.onChange(event.target.value)}
      onFocus={props.onFocus}
      data-testid={props.testId}
      data-autogrow={autoGrow ? 'true' : undefined}
      data-compact={props.compact === true ? 'true' : undefined}
    />
  );
}

export interface DropdownOption<K extends string> {
  key: K;
  label: string;
}

// Dropdown foundation (03 / 05 "Responsible Trade" / "Report Section"): a field-shaped trigger with
// a chevron; options open in a sheet (bottom on phone, centered on iPad — the KEEP sheet behaviour)
// with a Cancel in the header. The consumer supplies the options; nothing here is business data.
export function Dropdown<K extends string>(props: {
  id?: string;
  value: K | null;
  options: DropdownOption<K>[];
  onChange: (key: K) => void;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  testId?: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const current = props.options.find((o) => o.key === props.value) ?? null;
  return (
    <>
      <button
        id={props.id}
        type="button"
        className="dropdown"
        onClick={() => setOpen(true)}
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={props.testId}
      >
        <span className={`dropdown-value ${current ? '' : 'dropdown-placeholder'}`}>{current ? current.label : (props.placeholder ?? t('common.select'))}</span>
        <Icon name="keyboard_arrow_down" />
      </button>
      {open ? (
        <SheetShell title={props.title ?? t('common.select')} onClose={() => setOpen(false)} layer="over" testId={props.testId ? `${props.testId}-sheet` : undefined}>
          <div className="option-list" role="listbox">
            {props.options.map((option) => {
              const selected = option.key === props.value;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`option-row ${selected ? 'option-row-selected' : ''}`}
                  onClick={() => {
                    props.onChange(option.key);
                    setOpen(false);
                  }}
                  data-testid={props.testId ? `${props.testId}-option-${option.key}` : undefined}
                >
                  <span>{option.label}</span>
                  {selected ? <Icon name="check" /> : null}
                </button>
              );
            })}
          </div>
        </SheetShell>
      ) : null}
    </>
  );
}
